import { Tooltip } from '@design/components/tooltip/Tooltip';
import { MouseEvent, ReactNode, RefObject, useState } from 'react';
import { Mention } from '../types';
import { isMentionChipElement, readMentionFromChipElement } from './dom-chip';
import styles from './MentionComposerInput.module.scss';

export interface UseResolvedMentionPreviewArgs {
  editorRef: RefObject<HTMLDivElement | null>;
  /** Renders a hover preview for a resolved chip. Return `null` to show none. */
  renderPreview?: (mention: Mention) => ReactNode | null;
}

export interface UseResolvedMentionPreviewResult {
  onChipMouseOver: (e: MouseEvent<HTMLDivElement>) => void;
  onChipMouseOut: (e: MouseEvent<HTMLDivElement>) => void;
  clearHoveredChip: () => void;
  chipPreviewTooltip: ReactNode;
}

const findMentionChipFromTarget = (
  root: HTMLElement,
  target: EventTarget | null
): HTMLElement | null => {
  if (!(target instanceof Node)) {
    return null;
  }
  const el =
    target instanceof Element
      ? target.closest('[data-mention-chip]')
      : target.parentElement?.closest('[data-mention-chip]');
  if (!isMentionChipElement(el) || !root.contains(el)) {
    return null;
  }
  return el;
};

export const useResolvedMentionPreview = ({
  editorRef,
  renderPreview,
}: UseResolvedMentionPreviewArgs): UseResolvedMentionPreviewResult => {
  const [hoveredChip, setHoveredChip] = useState<HTMLElement | null>(null);
  const [hoveredChipRect, setHoveredChipRect] = useState<DOMRect | null>(null);

  const onChipMouseOver = (e: MouseEvent<HTMLDivElement>) => {
    const root = editorRef.current;
    if (!root) {
      return;
    }
    const el = findMentionChipFromTarget(root, e.target);
    if (el && el !== hoveredChip) {
      setHoveredChip(el);
      setHoveredChipRect(el.getBoundingClientRect());
    }
  };

  const onChipMouseOut = (e: MouseEvent<HTMLDivElement>) => {
    const root = editorRef.current;
    if (!root || !hoveredChip) {
      return;
    }
    const leaving = findMentionChipFromTarget(root, e.target);
    if (!leaving || leaving !== hoveredChip) {
      return;
    }
    const related = e.relatedTarget;
    if (related instanceof Node && hoveredChip.contains(related)) {
      return;
    }
    setHoveredChip(null);
    setHoveredChipRect(null);
  };

  const clearHoveredChip = () => {
    setHoveredChip(null);
    setHoveredChipRect(null);
  };

  const previewCard =
    hoveredChip?.isConnected && renderPreview
      ? renderPreview(readMentionFromChipElement(hoveredChip))
      : null;

  const chipPreviewTooltip =
    previewCard && hoveredChipRect ? (
      <Tooltip
        label={previewCard}
        isOpen
        inPortal
        strategy="fixed"
        placement="top"
        hasDelay={false}
        dismissible={false}
        trigger={[]}
        hasMaxWidth={false}
        hasArrow={false}
      >
        <span
          aria-hidden
          className={styles.unresolvedTooltipAnchor}
          style={{
            top: hoveredChipRect.top,
            left: hoveredChipRect.left,
            width: hoveredChipRect.width,
            height: hoveredChipRect.height,
          }}
        />
      </Tooltip>
    ) : null;

  return {
    onChipMouseOver,
    onChipMouseOut,
    clearHoveredChip,
    chipPreviewTooltip,
  };
};
